import React from 'react';
import { GraduationCap, Award, Globe, MapPin, Mail, Phone, Users, Search, BarChart3, Briefcase, FolderKanban, LineChart, ArrowRight, CheckCircle } from 'lucide-react';
import { personalInfo, about, education, expertise } from '../data/mock';
import { useInView } from '../hooks/useInView';

const AboutPage = () => {
  const [headerRef, headerInView] = useInView({ threshold: 0.1 });
  const [bioRef, bioInView] = useInView({ threshold: 0.1 });
  const [eduRef, eduInView] = useInView({ threshold: 0.1 });
  const [expertiseRef, expertiseInView] = useInView({ threshold: 0.1 });

  const getIcon = (iconName) => {
    const icons = {
      Users: Users,
      Search: Search,
      BarChart3: BarChart3,
      Vote: Award,
      Briefcase: Briefcase,
      FolderKanban: FolderKanban,
      LineChart: LineChart,
      GraduationCap: GraduationCap
    };
    const IconComponent = icons[iconName];
    return IconComponent ? <IconComponent className="w-7 h-7" /> : null;
  };

  const eduColors = [
    { gradient: "from-emerald-400 to-green-600", shadow: "shadow-emerald-500/30" },
    { gradient: "from-blue-400 to-indigo-600", shadow: "shadow-blue-500/30" },
    { gradient: "from-purple-400 to-violet-600", shadow: "shadow-purple-500/30" },
  ];

  const expertiseColors = [
    "from-emerald-400 to-green-600",
    "from-amber-400 to-orange-500",
    "from-blue-400 to-indigo-600",
    "from-purple-400 to-violet-600",
    "from-rose-400 to-pink-600",
    "from-cyan-400 to-teal-600",
    "from-red-400 to-rose-600",
    "from-indigo-400 to-blue-600",
  ];

  return (
    <>
      {/* Page Header - Colorful Design */}
      <div className="relative bg-gradient-to-br from-[#1F3C88] via-[#2A4FA8] to-[#1F3C88] py-32 overflow-hidden" ref={headerRef}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className={`transition-all duration-700 ${headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="inline-block px-5 py-2 bg-gradient-to-r from-emerald-400 to-green-600 text-white text-sm font-bold rounded-full mb-6 shadow-lg shadow-emerald-500/30">
              BACKGROUND
            </span>
            <h1 className="text-5xl lg:text-7xl font-black text-white mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              About <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-400">Me</span>
            </h1>
            <p className="text-white/80 max-w-2xl text-xl leading-relaxed font-medium">
              Academic leader, researcher, and consultant with over a decade of experience in higher education and institutional development.
            </p>
          </div>
        </div>
      </div>

      {/* Bio Section */}
      <section className="py-20 bg-white" ref={bioRef}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Image & Contact */}
            <div className={`transition-all duration-700 ${bioInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src={personalInfo.profileImage}
                    alt={personalInfo.name}
                    className="w-full h-[550px] object-cover object-top"
                  />
                </div>
                {/* Floating Badge */}
                <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-emerald-400 to-green-600 text-white rounded-2xl px-6 py-4 shadow-xl shadow-emerald-500/30">
                  <p className="text-4xl font-black" style={{ fontFamily: "'Montserrat', sans-serif" }}>10+</p>
                  <p className="text-sm font-semibold text-white/90">Years Experience</p>
                </div>
              </div>
              
              {/* Contact Info Cards */}
              <div className="mt-12 space-y-4">
                <a href={`mailto:${personalInfo.email}`} className="group flex items-center gap-4 p-5 rounded-2xl bg-gradient-to-r from-blue-400 to-indigo-600 shadow-xl shadow-blue-500/30 hover:scale-[1.02] transition-all duration-300">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-white/80 text-sm font-medium">Email</p>
                    <p className="text-white font-bold">{personalInfo.email}</p>
                  </div>
                </a>
                <a href={`tel:${personalInfo.phone}`} className="group flex items-center gap-4 p-5 rounded-2xl bg-gradient-to-r from-emerald-400 to-green-600 shadow-xl shadow-emerald-500/30 hover:scale-[1.02] transition-all duration-300">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-white/80 text-sm font-medium">Phone</p>
                    <p className="text-white font-bold">{personalInfo.phone}</p>
                  </div>
                </a>
                <div className="flex items-center gap-4 p-5 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 shadow-xl shadow-amber-500/30">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-white/80 text-sm font-medium">Location</p>
                    <p className="text-white font-bold">{personalInfo.location}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bio Content */}
            <div className={`transition-all duration-700 delay-200 ${bioInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="space-y-8">
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                  <h3 className="text-2xl font-black text-[#1C1C1C] mb-4 flex items-center gap-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    <span className="w-12 h-1.5 bg-gradient-to-r from-emerald-400 to-green-600 rounded-full"></span>
                    Academic Identity
                  </h3>
                  <p className="text-[#4A5568] leading-relaxed text-lg">{about.intro}</p>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                  <h3 className="text-2xl font-black text-[#1C1C1C] mb-4 flex items-center gap-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    <span className="w-12 h-1.5 bg-gradient-to-r from-blue-400 to-indigo-600 rounded-full"></span>
                    Research Mission
                  </h3>
                  <p className="text-[#4A5568] leading-relaxed text-lg">{about.mission}</p>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                  <h3 className="text-2xl font-black text-[#1C1C1C] mb-4 flex items-center gap-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    <span className="w-12 h-1.5 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full"></span>
                    Leadership Philosophy
                  </h3>
                  <p className="text-[#4A5568] leading-relaxed text-lg">{about.philosophy}</p>
                </div>

                {/* Languages */}
                <div className="rounded-3xl p-8 bg-gradient-to-br from-purple-400 to-violet-600 text-white shadow-xl shadow-purple-500/30">
                  <h3 className="text-2xl font-black mb-6 flex items-center gap-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    <Globe className="w-7 h-7" />
                    Languages
                  </h3>
                  <div className="space-y-5">
                    {about.languages.map((lang, index) => (
                      <div key={index}>
                        <div className="flex justify-between mb-2">
                          <span className="font-bold">{lang.language}</span>
                          <span className="text-white/80 text-sm font-medium">{lang.level}</span>
                        </div>
                        <div className="h-3 bg-white/20 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-amber-300 to-orange-400 rounded-full transition-all duration-1000"
                            style={{ width: bioInView ? `${lang.percentage}%` : '0%' }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Education Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white" ref={eduRef}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-700 ${eduInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="inline-block px-5 py-2 bg-gradient-to-r from-blue-400 to-indigo-600 text-white text-sm font-bold rounded-full mb-4 shadow-lg shadow-blue-500/30">
              EDUCATION
            </span>
            <h2 className="text-4xl lg:text-5xl font-black text-[#1C1C1C] mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Academic <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600">Journey</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {education.map((edu, index) => (
              <div
                key={index}
                className={`group rounded-3xl p-8 bg-gradient-to-br ${eduColors[index % eduColors.length].gradient} overflow-hidden transition-all duration-500 hover:scale-105 hover:-translate-y-2 shadow-xl ${eduColors[index % eduColors.length].shadow} ${eduInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                  <GraduationCap className="w-8 h-8 text-white" />
                </div>
                <span className="inline-block px-3 py-1 bg-white/20 text-white text-sm font-bold rounded-full mb-4">
                  {edu.period}
                </span>
                <h3 className="text-xl font-black text-white mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>{edu.degree}</h3>
                <p className="text-white/90 font-semibold text-sm mb-3">{edu.institution}</p>
                <p className="text-white/80 text-sm leading-relaxed">{edu.description}</p>
                {edu.honors && (
                  <div className="mt-6 flex items-center gap-2 text-white">
                    <Award className="w-5 h-5" />
                    <span className="font-bold">{edu.honors}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 bg-white" ref={expertiseRef}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-700 ${expertiseInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="inline-block px-5 py-2 bg-gradient-to-r from-purple-400 to-violet-600 text-white text-sm font-bold rounded-full mb-4 shadow-lg shadow-purple-500/30">
              SKILLS & EXPERTISE
            </span>
            <h2 className="text-4xl lg:text-5xl font-black text-[#1C1C1C] mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Areas of <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-violet-600">Specialization</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {expertise.map((item, index) => (
              <div
                key={item.id}
                className={`group rounded-3xl p-8 bg-gradient-to-br ${expertiseColors[index % expertiseColors.length]} overflow-hidden transition-all duration-500 hover:scale-105 hover:-translate-y-2 shadow-xl ${expertiseInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                    <span className="text-white">
                      {getIcon(item.icon)}
                    </span>
                  </div>
                  <h3 className="text-lg font-black text-white mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {item.title}
                  </h3>
                  <p className="text-white/80 text-sm leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
